import { useState, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  ChevronDown,
  Eye,
  RefreshCw,
  Download,
  Calendar,
  X,
} from 'lucide-react';
import { format } from 'date-fns';
import { useOrders } from '../hooks/useOrders';
import { clsx } from 'clsx';

type OrderStatus = 'pending' | 'processing' | 'ready' | 'error' | 'all';

const statusOptions: { value: OrderStatus; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'ready', label: 'Ready' },
  { value: 'error', label: 'Error' },
];

const customerOptions = [
  { value: 'all', label: 'All Customers' },
  { value: 'ddc-foods', label: 'DDC Foods' },
  { value: 'creed', label: 'Creed Foodservice' },
  { value: 'sainsbury', label: 'Sainsbury' },
  { value: 'bidfood', label: 'Bidfood' },
  { value: 'brakes', label: 'Brakes' },
];

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  processing: 'bg-blue-100 text-blue-700',
  ready: 'bg-perfectted-light text-perfectted-dark',
  error: 'bg-red-100 text-red-700',
};

export default function OrdersPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus>(
    (searchParams.get('status') as OrderStatus) || 'all'
  );
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const { data: orders, isLoading, refetch, isRefetching } = useOrders();

  // Filter orders
  const filteredOrders = useMemo(() => {
    if (!orders) return [];

    return orders.filter((order) => {
      // Status filter
      if (selectedStatus !== 'all' && order.status !== selectedStatus) {
        return false;
      }

      // Customer filter
      if (selectedCustomer !== 'all' && order.customerId !== selectedCustomer) {
        return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesPO = order.poNumber.toLowerCase().includes(query);
        const matchesCustomer = order.customerName.toLowerCase().includes(query);
        if (!matchesPO && !matchesCustomer) {
          return false;
        }
      }

      // Date filters
      if (dateFrom && new Date(order.orderDate) < new Date(dateFrom)) {
        return false;
      }
      if (dateTo && new Date(order.orderDate) > new Date(dateTo)) {
        return false;
      }

      return true;
    });
  }, [orders, selectedStatus, selectedCustomer, searchQuery, dateFrom, dateTo]);

  const clearFilters = () => {
    setSelectedStatus('all');
    setSelectedCustomer('all');
    setDateFrom('');
    setDateTo('');
    setSearchQuery('');
    setSearchParams({});
  };

  const hasActiveFilters = selectedStatus !== 'all' || selectedCustomer !== 'all' || dateFrom || dateTo || searchQuery;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Orders</h2>
          <p className="text-gray-500 mt-1">Manage and track purchase orders</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            disabled={isRefetching}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={clsx('w-4 h-4', isRefetching && 'animate-spin')} />
            Refresh
          </button>
          <button className="flex items-center gap-2 px-4 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Search and filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search input */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by PO# or customer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary focus:border-transparent"
            />
          </div>

          {/* Status filter */}
          <div className="relative">
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value as OrderStatus)}
              className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary focus:border-transparent bg-white"
            >
              {statusOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* Customer filter */}
          <div className="relative">
            <select
              value={selectedCustomer}
              onChange={(e) => setSelectedCustomer(e.target.value)}
              className="appearance-none pl-4 pr-10 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary focus:border-transparent bg-white"
            >
              {customerOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          {/* More filters toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2.5 border rounded-lg transition-colors',
              showFilters
                ? 'bg-perfectted-light border-perfectted-primary text-perfectted-dark'
                : 'border-gray-200 text-gray-600 hover:bg-gray-50'
            )}
          >
            <Filter className="w-4 h-4" />
            More Filters
          </button>

          {/* Clear filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-2 px-4 py-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Clear
            </button>
          )}
        </div>

        {/* Expanded filters */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600">Date range:</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
              />
            </div>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        Showing {filteredOrders.length} of {orders?.length ?? 0} orders
      </div>

      {/* Orders table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">PO Number</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Customer</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Order Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Delivery Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Items</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Total</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <RefreshCw className="w-5 h-5 animate-spin text-perfectted-primary" />
                      <span className="text-gray-500">Loading orders...</span>
                    </div>
                  </td>
                </tr>
              ) : filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                    No orders found matching your criteria
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <Link
                        to={`/orders/${order.id}`}
                        className="font-medium text-perfectted-primary hover:text-perfectted-dark"
                      >
                        {order.poNumber}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-gray-900">{order.customerName}</td>
                    <td className="px-6 py-4">
                      <span className={clsx('px-2.5 py-1 rounded-full text-xs font-medium', statusColors[order.status])}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {format(new Date(order.orderDate), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {order.deliveryDate ? format(new Date(order.deliveryDate), 'dd MMM yyyy') : '-'}
                    </td>
                    <td className="px-6 py-4 text-gray-600">{order.itemCount}</td>
                    <td className="px-6 py-4 text-gray-900 font-medium">
                      {order.total ? `${order.total.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/orders/${order.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-perfectted-primary hover:bg-perfectted-light rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
